<script lang="ts">
  import { createEventDispatcher, onMount } from 'svelte';

  import SelectRow from '@/comps-sdk/components/select-row/src/pc-select-row.svelte';
  import { toast } from '@/comps-sdk/components/toast';
  import {
    getRecentZoneWithRole,
    LocalZoneRoleDataService,
    ZoneRoleSelectService,
  } from '@/comps-sdk/components/zone-role-select/services';
  import UserInfo from '@/comps-sdk/components/zone-role-select/src/views/pc/user-info.svelte';
  import { CODE, GAME_TYPE } from '@/comps-sdk/constants';
  import { type ICommon, type IZoneRoleSelect } from '@/comps-sdk/interface';
  import bridge from '@/comps-sdk/modules/bridge';
  import { isEmptyObject } from '@/comps-sdk/modules/iwan-common-utils';

  const logPrefix = 'PC区服组件';

  export let props: IZoneRoleSelect.ZoneRoleSelectProps;

  const { getUserInfoCb, gameId, gameAppId, gameType, alias, scene, hasWxAppid } = props;
  const dispatch = createEventDispatcher();

  // 客户端
  const clients: Array<IZoneRoleSelect.PanelItem> = [
    {
      name: '安卓',
      id: 'android',
    },
    {
      name: 'iOS',
      id: 'ios',
    },
    {
      name: 'pc',
      id: 'pc',
    },
  ];
  // 大区
  let zones: Array<IZoneRoleSelect.PanelItem> = [];
  // 角色
  let roles: Array<IZoneRoleSelect.PanelItem> = [];

  // 已选择好的区服数据
  const selectedValue: IZoneRoleSelect.SelectedValueType = {
    client: {
      id: '',
      name: '',
    },
    area: {
      id: '',
      name: '',
    },
    role: {
      id: '',
      name: '',
    },
  };
  export const getSelectedValue = () => selectedValue;

  // 登录类型
  let loginType = '';
  // 是否展示区服选项
  let isShowZone = false;
  // 是否展示角色选项
  let isShowRole = false;

  let userInfo: ICommon.UserInfo | null = {
    type: '',
    nickname: '',
    headImgUrl: '',
  };
  const getUserInfo = async () => {
    try {
      userInfo = getUserInfoCb ? await getUserInfoCb() : null;
      console.log(logPrefix, '用户信息:', userInfo);
    } catch (e) {
      console.error(logPrefix, '获取用户信息失败:', e);
    }
  };

  const localService = new LocalZoneRoleDataService(props);

  let zoneRoleSelectService: ZoneRoleSelectService | null;
  const initZoneRoleSelectService = async () => {
    const localInfo: any = await localService.getData();
    zoneRoleSelectService = new ZoneRoleSelectService({
      gameType,
      gameId,
      gameAppId,
      alias,
      scene,
      hasWxAppid,
      clientId: localInfo?.clientId || '',
      onChange: (type: string, changed: IZoneRoleSelect.PanelItem) => {
        console.log(logPrefix, '区服角色选择Service.onChange', type, changed);
        selectedValue[type] = changed;
      },
    });
  };

  let activeZoneId = '';
  const setActiveZone = () => {
    if (activeZoneId) {
      for (let i = 0, len = zones.length; i < len; i++) {
        if (activeZoneId === zones[i].id) {
          zoneRoleSelectService!.setSelectedValue('area', zones[i]);
          break;
        }
      }
    }
  };

  const getZoneInfo = async () => {
    try {
      if (!loginType) {
        const { result: mainLoginType } = await bridge.getMainLoginType();
        loginType = mainLoginType;
      }
      const { data, code, msg } = await zoneRoleSelectService!.getZoneInfo(loginType);
      if (CODE.success.includes(code) && data) {
        zones = data.zones;
        isShowRole = data.isShowRole;
        isShowZone = data.isShowZone;
        if (data.isNeedGetRole) {
          getRoleInfo();
        }
      } else {
        toast(msg || '获取大区信息失败，数据为空');
      }
    } catch (e) {
      console.error(logPrefix, '获取大区信息失败:', e);
      toast('获取大区信息失败，网络错误，请稍后再试');
    }
  };

  // 获取最近选择过的区服
  const getRemoteZoneInfo = async (clientName: string) => {
    const sys = clientName === 'android' ? '1' : '2';
    return await getRecentZoneWithRole({
      game_id: gameId,
      sys,
    });
  };

  // 设置最近选择过的区服
  // 优先从远程拉取，远程没拉到再读本地
  const setRemoteZoneInfo = async (clientName: string) => {
    const recentZoneInfo = await getRemoteZoneInfo(clientName);
    const recentZoneId = recentZoneInfo?.partition_info?.partition;

    if (recentZoneId) {
      activeZoneId = recentZoneId;
      setActiveZone();
      return;
    }

    const localInfo: any = await localService.getData();
    if (localInfo) {
      activeZoneId = localInfo.areaId;
      setActiveZone();
    }
  };

  const getRoleInfo = async () => {
    try {
      const { roles: remoteRoles, code, msg } = await zoneRoleSelectService!.getRoleInfo();

      if (CODE.notAuth.includes(code)) {
        toast(msg);
        return;
      }

      if (CODE.success.includes(code)) {
        if (!remoteRoles || remoteRoles.length === 0) {
          toast('该区服暂无角色');
          return;
        }

        roles = remoteRoles;

        localService.setData({
          clientId: selectedValue.client.id,
          areaId: selectedValue.area.id,
        });

        return;
      }

      toast(msg);
    } catch (e) {
      toast('获取角色信息失败，网络错误，请稍后再试');
    }
  };

  const handleSelectChange = (type: string, option: IZoneRoleSelect.PanelItem) => {
    console.log(logPrefix, 'setSelectedValue', type, option);
    zoneRoleSelectService?.setSelectedValue(type, option);
  };

  let lastClientId = '';
  // watches
  $: {
    // 客户端变化之后，同步更新区服列表/角色列表，并设置最近选过的区服
    if (selectedValue.client.id && lastClientId !== selectedValue.client.id) {
      console.log(logPrefix, 'selectedValue.client.id变化:', selectedValue.client.id);
      lastClientId = selectedValue.client.id;
      getZoneInfo().then(() => {
        setRemoteZoneInfo(selectedValue.client.id);
      });
    }
  }
  let lastAreaId = '';
  $: {
    // 区服变化之后，同步获取角色信息
    if (selectedValue.area.id && selectedValue.area.id !== '-1' && selectedValue.area.id !== lastAreaId) {
      console.log(logPrefix, 'selectedValue.area.id变化:', selectedValue.area.id);
      lastAreaId = selectedValue.area.id;
      getRoleInfo();
    }
  }

  // computed
  $: changeClient = gameType === GAME_TYPE.mobile;
  $: hasUserInfo = !isEmptyObject(userInfo);
  $: {
    console.log(logPrefix, 'selectedValue变化: ', selectedValue);
    const { client, area, role } = selectedValue;

    if (isShowRole) {
      dispatch('confirm-status-change', !area.id || !role.id || !client.id);
    } else if (isShowZone && (!area.id || !client.id)) {
      dispatch('confirm-status-change', !area.id || !client.id);
    }

    dispatch('confirm-status-change', !client.id);
  }

  onMount(async () => {
    getUserInfo();
    initZoneRoleSelectService();
    setActiveZone();
  });
</script>

<div class="panel-wrapper">
  <div>
    {#if hasUserInfo}
      <UserInfo {userInfo} />
    {/if}
    <div class={`select-wrapper ${hasUserInfo ? '' : 'without-userinfo'}`}>
      <SelectRow
        label="客户端"
        disabled={!changeClient}
        isShowSearch={false}
        selectedValue={selectedValue.client}
        options={clients}
        on:selectChange={(e) => {
          handleSelectChange('client', e.detail);
        }}
      />

      {#if isShowZone}
        <SelectRow
          label="区服"
          isShowSearch={true}
          selectedValue={selectedValue.area}
          options={zones}
          on:selectChange={(e) => {
            handleSelectChange('area', e.detail);
          }}
        >
          <div slot="notFoundContent" class="default-panel">
            <div class="svg-icon svg-icon-default">
              <img class="icon-img" src="https://puui.qpic.cn/iwan_cloud/1/8b9b53ea178f03c86dabc58053053ce9" alt="" />
            </div>
            <div class="default-text">暂无区服</div>
          </div>
        </SelectRow>
      {/if}
      {#if isShowRole}
        <SelectRow
          label="角色"
          options={roles}
          isShowSearch={true}
          selectedValue={selectedValue.role}
          on:selectChange={(e) => {
            handleSelectChange('role', e.detail);
          }}
        >
          <div slot="notFoundContent" class="default-panel">
            <div class="svg-icon svg-icon-default">
              <img class="icon-img" src="https://puui.qpic.cn/iwan_cloud/1/8b9b53ea178f03c86dabc58053053ce9" alt="" />
            </div>
            <div class="default-text">暂无角色</div>
          </div>
        </SelectRow>
      {/if}
    </div>
  </div>
</div>

<style lang="scss">
  .panel-wrapper {
    padding: 0 10px;
    .select-wrapper {
      display: flex;
      flex-flow: column;
      justify-content: center;
      &.without-userinfo {
        margin-top: 70px;
      }
    }
  }

  .default-panel {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding-bottom: 10px;
    .default-text {
      font-family: PingFangSC-Regular;
      font-weight: 400;
      font-size: 14px;
      color: rgba(25, 25, 25, 0.5);
    }
  }

  .svg-icon-default {
    height: 130px;
    width: 130px;
    .icon-img {
      width: 100%;
      overflow: hidden;
    }
  }
</style>
